// Krok (A) potoku: tekst→tekst, wykonywany PRZED parserem ChordSheetJS.
//
// Obchodzi pułapkę 1 i 3 opisane w planie: parser zna tylko cztery angielskie
// rodziny nagłówków (Verse/Chorus/Bridge/zbiorczy Intro|Outro|Instrumental|
// Interlude|Solo|Pre-Chorus) i tylko w nawiasach kwadratowych, a linie tabulatury
// w wielkiej literze struny (np. `E|---3---5---`) przechodzą jako akord.
//
// Odpowiedzialności, w tej kolejności:
// 1. rozbrojenie markupu UG: `[tab]`/`[/tab]`/`[ch]`/`[/ch]`;
// 2. rozpoznanie nagłówków sekcji PL/EN (w tym skrótów typu [V1], [Ch], [Br], oraz
//    wariantów bez nawiasów typu "Refren:", "Chorus:"), przepisanie na kanoniczne
//    formy zrozumiałe dla `UltimateGuitarParser`. Mechanizm biblioteki jest tu
//    inny, niż pierwotnie zakładał plan: linia "Chorus:" bez nawiasu NIE ląduje
//    jako zwykły tekst piosenki, tylko jako AKORD o nazwie "Chorus:", bo
//    `CHORD_LINE_REGEX` biblioteki ma sufiks `[^/\s]*`, który dopuszcza po "C"
//    (rdzeń akordu) dowolny dalszy ciąg bez ukośnika i spacji, w tym "horus:".
//    Zweryfikowane empirycznie na chordsheetjs@16.2.2 przez koordynatora, patrz
//    test "nagłówki bez nawiasów po polsku i angielsku" niżej;
// 3. tłumaczenie polskich metadanych (`Tonacja:`, `Kapodaster:`, `Metrum:`, `Tempo:`,
//    `Wykonawca:`, `Tytuł:`/`Song:`) na tagi z `META_TAGS`;
// 4. wycięcie linii tabulatury nutowej (np. `e|---3---5---`);
// 5. przechwycenie i usunięcie linii `Bicie:`/`Rytm:`/`Strumming:`.
//
// Wzorce nagłówków i metadanych używają `\p{L}` z flagą `u`, NIGDY `\w`
// (w JavaScripcie `\w` jest ASCII i gubi polską odmianę, patrz
// reference_w_w_javascripcie_jest_ascii_wiec_polska_odmiana_wypada w pamięci serwera).
//
// KOREKTA 2026-08-27 (audyt Codexa na chordsheetjs@16.2.2, zainstalowana wersja):
// pierwotny plan zakładał, że po zdjęciu `[ch]`/`[/ch]` trzeba dopełnić spacjami do
// pierwotnej szerokości znacznika (9 znaków), żeby akord "nie przesunął się w lewo".
// To było błędne. Autor taba w edytorze UG widzi tekst BEZ znaczników i tak dobiera
// odstępy między akordami, więc znaczniki są semantycznie zerowej szerokości: zwykłe
// zdjęcie ich (bez podstawiania czegokolwiek w zamian) odtwarza dokładnie ten układ.
// Dopełnianie spacjami kumulowałoby fałszywe przesunięcie w prawo przy każdym
// kolejnym akordzie w tej samej linii. Zweryfikowane na `jolka-jolka-pamietasz.txt`:
// zwykłe zdjęcie znaczników stawia D, A i Bm dokładnie nad początkami kolejnych słów
// linii tekstu pod spodem.
//
// Ta sama uwaga dotyczy linii, w której `[ch]` owija fragment ZWYKŁEGO tekstu (a nie
// linii akordów) - format UG w praktyce tego nie robi (akordy zawsze siedzą we własnej
// linii), ale gdyby się zdarzyło, zwykłe zdjęcie znaczników jest wciąż bezpieczne: nie
// przesuwa niczego poza to, co i tak zniknęłoby z ekranu UG. Nie ma więc potrzeby
// rozróżniania "linii akordów" od "linii tekstu" na tym etapie, transformacja jest
// tym samym prostym zdjęciem znaczników niezależnie od typu linii.

/** Rodzaj sekcji, jaki `preNormalize` potrafi rozpoznać w nagłówku. */
export type HeaderKind =
  | 'intro'
  | 'verse'
  | 'prechorus'
  | 'chorus'
  | 'bridge'
  | 'outro'
  | 'instrumental'
  | 'solo'
  | 'interlude';

/** Jedno trafienie nagłówka: co autor taba napisał i jak to zrozumieliśmy. */
export interface HeaderHit {
  /** Numer linii w tekście PO normalizacji (`PreNormalizeResult.text`), 0-based. */
  lineIndex: number;
  /** Dokładny oryginalny tekst nagłówka, bez nawiasów/dwukropka, np. "Vese 3", "Refren". */
  original: string;
  kind: HeaderKind;
  /** Numer sekcji, gdy autor go podał (np. "Verse 2" → 2), inaczej null. */
  number: number | null;
  /** Etykieta wyświetlana w interfejsie, np. "Z1", "R", "Most". */
  label: string;
  /** true, gdy dopasowanie poszło przez tolerancję literówek (odległość edycyjna 1). */
  isTypo: boolean;
  /**
   * Powtórzenie zapisane wprost przy nagłówku (np. "Chorus x2" → 2), inaczej null.
   * To NIE jest numer sekcji: "Refren x2" nie jest drugim refrenem, tylko jednym
   * refrenem granym dwukrotnie.
   */
  times: number | null;
  /**
   * Dopisek opisowy po nawiasie kwadratowym, np. "[Middle] (HARMONICA SOLO)"
   * → "HARMONICA SOLO". NIE wpływa na rozpoznanie `kind`, to czysta etykieta
   * opisowa. `null`, gdy nagłówek nie miał dopisku.
   */
  description: string | null;
  /**
   * true, gdy WEWNĄTRZ tej sekcji (między tym nagłówkiem a następnym) wycięto
   * choć jedną linię tabulatury nutowej. Odróżnia sekcję naprawdę zapisaną
   * tabulaturą od sekcji po prostu pustej - `compile.ts` używa tego do
   * `TAB_ONLY_SECTION` zamiast zgadywać z samej pustki treści, patrz
   * `hadTabLinesRemoved` w `RawSection` (`segment.ts`).
   */
  hadTabLinesRemoved: boolean;
}

export interface PreNormalizeResult {
  /** Tekst gotowy do podania do `UltimateGuitarParser().parse()`. */
  text: string;
  /** Tabela trafień nagłówków, w kolejności występowania. */
  headers: readonly HeaderHit[];
  /** Zawartość linii `Bicie:`/`Rytm:`/`Strumming:`, wycięta z treści przed parserem. */
  strumming: string | null;
  /**
   * Nieformalna wzmianka o kapodastrze w PREAMBULE (przed pierwszym prawdziwym
   * nagłówkiem sekcji), np. "Make sure you have your CAPO ON 2ND FRET"
   * (shape-of-my-heart.txt). `null`, gdy nie było żadnej wzmianki. `fret` niesie
   * numer progu, gdy dało się go wyciągnąć ze zdania, inaczej `null` (autor
   * wspomniał kapodaster, ale nie podał numeru - `compile.ts` NIE zgaduje,
   * tylko podnosi diagnostykę ostrzegawczą). Formalne "Capo: N" ma zawsze
   * pierwszeństwo, ustalane w `compile.ts`.
   */
  informalCapo: { fret: number | null } | null;
  /** Liczba linii rozpoznanych jako tabulatura nutowa i wyciętych. */
  tabLinesRemoved: number;
  /**
   * Liczba linii rozpoznanych jako diagram chwytu (nazwa akordu plus ciąg
   * cyfr/iksów/myślników, np. "G 3-x-0-0-3(3)") i wyciętych, osobno od
   * `tabLinesRemoved`: to inny zapis niż tabulatura sześciolinijkowa.
   */
  chordDiagramLinesRemoved: number;
  /** Liczba znaczników `[ch]` w źródle, przed rozbrojeniem markupu. */
  chordTagCount: number;
}

interface HeaderFamily {
  kind: HeaderKind;
  /** Pełne nazwy PL/EN, dopasowywane dokładnie (po normalizacji wielkości liter). */
  words: string[];
  /** Skróty jednoliterowe/dwuliterowe, dopasowywane TYLKO dokładnie, nigdy rozmyto. */
  aliases: string[];
}

// Kolejność ma znaczenie tam, gdzie nazwy mogłyby się nakładać: pre-chorus przed
// chorus, zwrotka przed wers. W praktyce dopasowanie jest zawsze dokładne (całe słowo),
// więc nakładanie nie jest tu ryzykiem technicznym, ale zachowujemy kolejność z planu
// dla czytelności i zgodności z jego opisem.
const HEADER_FAMILIES: readonly HeaderFamily[] = [
  { kind: 'prechorus', words: ['prechorus', 'pre-chorus', 'pre chorus', 'przedrefren'], aliases: [] },
  { kind: 'chorus', words: ['chorus', 'refren'], aliases: ['c', 'ch', 'r'] },
  { kind: 'verse', words: ['verse', 'zwrotka', 'zwrotki', 'wers'], aliases: ['v'] },
  // "middle" (angielski tab "shape-of-my-heart.txt": "[Middle] (HARMONICA SOLO)")
  // to synonim mostu, spoza pierwotnego słownika, znaleziony w korpusie.
  { kind: 'bridge', words: ['bridge', 'most', 'middle'], aliases: ['b', 'br'] },
  { kind: 'outro', words: ['outro', 'zakończenie', 'zakonczenie'], aliases: ['o'] },
  { kind: 'intro', words: ['intro', 'wstęp', 'wstep'], aliases: ['i'] },
  { kind: 'solo', words: ['solo'], aliases: [] },
  { kind: 'interlude', words: ['interlude'], aliases: [] },
  { kind: 'instrumental', words: ['instrumental'], aliases: [] },
];

// Znane fałszywe trafienia tolerancji literówek: słowa realnie odległe o 1 znak od
// nazwy rodziny, ale będące czymś zupełnie innym. "Soli" to muzyczny termin (liczba
// mnoga od "solo" we włoskim/polskim żargonie), nie literówka "Solo".
const TYPO_DENYLIST = new Set(['soli']);

const DISPLAY_LABEL: Record<HeaderKind, (n: number | null) => string> = {
  verse: (n) => (n === null ? 'Z' : `Z${n}`),
  chorus: () => 'R',
  prechorus: () => 'P',
  bridge: () => 'Most',
  outro: () => 'O',
  intro: () => 'I',
  solo: () => 'Solo',
  interlude: () => 'Int',
  instrumental: () => 'Instr',
};

// Kanoniczna forma bracketowa zrozumiała dla `UltimateGuitarParser`. Tylko `verse`
// niesie numer, bo tylko wzorzec `/^\[(Verse.*)]/i` biblioteki go akceptuje, reszta
// rodzin jest dopasowywana jednym zbiorczym wzorcem bez numeru.
function canonicalHeaderText(kind: HeaderKind, number: number | null): string {
  if (kind === 'verse') {
    return number === null ? '[Verse]' : `[Verse ${number}]`;
  }
  const names: Record<Exclude<HeaderKind, 'verse'>, string> = {
    chorus: 'Chorus',
    prechorus: 'Pre-Chorus',
    bridge: 'Bridge',
    outro: 'Outro',
    intro: 'Intro',
    solo: 'Solo',
    interlude: 'Interlude',
    instrumental: 'Instrumental',
  };
  return `[${names[kind as Exclude<HeaderKind, 'verse'>]}]`;
}

/** Odległość edycyjna Levenshteina, DP o dwóch wierszach, wystarcza dla krótkich słów. */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array<number>(n + 1);
  let curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j += 1) prev[j] = j;
  for (let i = 1; i <= m; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j]! + 1,
        curr[j - 1]! + 1,
        prev[j - 1]! + cost,
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n]!;
}

interface HeaderMatch {
  kind: HeaderKind;
  number: number | null;
  isTypo: boolean;
}

/**
 * Próbuje rozpoznać etykietę nagłówka (bez nawiasów/dwukropka). `allowTypo` włącza
 * tolerancję literówek, dopuszczalną WYŁĄCZNIE dla formy bracketowej (plan: "gdy
 * linia stoi w nawiasie kwadratowym i jest krótka"), nigdy dla aliasów jednoliterowych
 * i nigdy dla formy z dwukropkiem bez nawiasu.
 */
function matchHeaderLabel(rawLabel: string, allowTypo: boolean): HeaderMatch | null {
  const label = rawLabel.trim();
  if (label === '') return null;

  const numberMatch = label.match(/\d+/);
  const number = numberMatch ? Number.parseInt(numberMatch[0], 10) : null;
  const wordPart = label.replace(/\d+/g, '').replace(/[\s\-.:]+/g, ' ').trim().toLowerCase();
  if (wordPart === '') return null;

  // 1. Aliasy jednoliterowe/dwuliterowe: dopasowanie WYŁĄCZNIE dokładne.
  for (const family of HEADER_FAMILIES) {
    if (family.aliases.includes(wordPart)) {
      return { kind: family.kind, number, isTypo: false };
    }
  }

  // 2. Pełne nazwy: dopasowanie dokładne.
  for (const family of HEADER_FAMILIES) {
    if (family.words.includes(wordPart)) {
      return { kind: family.kind, number, isTypo: false };
    }
  }
  // "instrumental" po polsku odmienia się przez rodzaj (instrumentalna, instrumentalny),
  // rdzeń zostaje ten sam, więc łapiemy go prefiksem zamiast dopisywać każdą formę.
  if (wordPart.startsWith('instrumental')) {
    return { kind: 'instrumental', number, isTypo: false };
  }

  // 2b. Zapowiedź zdaniem. Autorzy tabów zapowiadają część utworu całym zdaniem,
  // nie samą nazwą: `nie-placz-ewka.txt` ma w linii 26 "And now chorus:", które
  // otwiera instruktażowy blok refrenu. Rozpoznajemy, gdy etykieta ma od dwóch do
  // czterech słów, a OSTATNIE z nich jest dokładną nazwą rodziny. Warunek długości
  // trzyma to przy zapowiedziach, a nie przy zdaniach tekstu utworu; zwykły wers
  // i tak rzadko kończy się słowem "chorus" i jeszcze rzadziej dwukropkiem, bo
  // linia musiała wcześniej przejść przez wzorzec wymagający dwukropka na końcu.
  const words = wordPart.split(' ');
  if (words.length >= 2 && words.length <= 4) {
    const last = words[words.length - 1]!;
    for (const family of HEADER_FAMILIES) {
      if (family.words.includes(last)) {
        return { kind: family.kind, number, isTypo: false };
      }
    }
  }

  // 3. Tolerancja literówek: tylko forma bracketowa, tylko słowa >= 4 litery, tylko
  // krótkie etykiety, nigdy znane fałszywe tropy, i tylko gdy jest DOKŁADNIE jeden
  // najlepszy kandydat w odległości 1 (remis między rodzinami = brak dopasowania).
  // Ograniczenie znane i świadome: sama odległość Levenshteina nie łapie przestawień
  // liter (np. "Brigde" względem "Bridge" to odległość 2, nie 1), więc taki przypadek
  // nie zostanie tu rozpoznany.
  if (!allowTypo) return null;
  if (wordPart.length < 4 || wordPart.length > 15) return null;
  if (TYPO_DENYLIST.has(wordPart)) return null;

  let bestDistance = Infinity;
  let bestKinds = new Set<HeaderKind>();
  for (const family of HEADER_FAMILIES) {
    for (const word of family.words) {
      const d = levenshtein(wordPart, word);
      if (d < bestDistance) {
        bestDistance = d;
        bestKinds = new Set([family.kind]);
      } else if (d === bestDistance) {
        bestKinds.add(family.kind);
      }
    }
  }
  if (bestDistance !== 1 || bestKinds.size !== 1) return null;
  const [kind] = bestKinds;
  return { kind: kind!, number, isTypo: true };
}

const METADATA_MAP: Record<string, string> = {
  tonacja: 'Key',
  key: 'Key',
  kapodaster: 'Capo',
  kapo: 'Capo',
  capo: 'Capo',
  metrum: 'Time',
  takt: 'Time',
  time: 'Time',
  tempo: 'Tempo',
  bpm: 'Tempo',
  wykonawca: 'Artist',
  artist: 'Artist',
  'tytuł': 'Title',
  tytul: 'Title',
  song: 'Title',
  title: 'Title',
  // Autorzy (13.10.2026): bez tłumaczenia „lyrics: Adam Ziemianin” zostawało
  // w tekście jak wers do zaśpiewania, w pełnym tekście i w śledzeniu śpiewu.
  lyrics: 'Lyricist',
  words: 'Lyricist',
  lyricist: 'Lyricist',
  'słowa': 'Lyricist',
  slowa: 'Lyricist',
  'sł.': 'Lyricist',
  music: 'Composer',
  muzyka: 'Composer',
  'muz.': 'Composer',
  composer: 'Composer',
  kompozytor: 'Composer',
};

const METADATA_LINE_RE =
  /^\s*(tonacja|key|kapodaster|kapo|capo|metrum|takt|time|tempo|bpm|wykonawca|artist|tytuł|tytul|song|title|lyrics|words|lyricist|słowa|slowa|sł\.|music|muzyka|muz\.|composer|kompozytor)\s*:\s*(.+?)\s*$/iu;

const STRUMMING_LINE_RE = /^\s*(bicie|rytm|strumming)\s*:\s*(.*?)\s*$/iu;

// Nieformalna zapowiedź kapodastra CAŁYM zdaniem, PL/EN, np. "Make sure you have
// your CAPO ON 2ND FRET" (shape-of-my-heart.txt). Formalne "Capo: N" (przez
// METADATA_MAP wyżej) ma zawsze pierwszeństwo - to jest tylko dodatkowy sygnał z
// preambuły, zbierany BEZ ruszania tekstu (zdanie zostaje w wyjściu tak jak było).
// Skrócone rdzenie ("kapodast"/"capo"/"kapo") łapią polską odmianę przez przyrostek
// otwarty, ale to bezpieczne: "kapodaster/kapodastra/kapodastrze" nie mają polskich
// znaków diakrytycznych w odmianie, więc zwykłe `\w*` (ASCII) nie gubi tu niczego.
const CAPO_MENTION_RE = /\b(kapodast\w*|capo|kapo)\b/iu;
const CAPO_DIGIT_RE = /\d+/;
const ROMAN_TO_ARABIC: Readonly<Record<string, number>> = {
  xii: 12, xi: 11, x: 10, ix: 9, viii: 8, vii: 7, vi: 6, v: 5, iv: 4, iii: 3, ii: 2, i: 1,
};
// Liczebnik rzymski TYLKO tuż przed słowem "prog(u)" (polska notacja "II prog"),
// żeby nie łapać przypadkowo zaimka "I" ani innych krótkich słów w zdaniu.
const CAPO_ROMAN_PROG_RE = /\b(xii|xi|ix|viii|vii|vi|iv|iii|ii|x|v|i)\b\s*prog/iu;

/** Rozpoznaje nieformalną wzmiankę o kapodastrze w JEDNEJ linii preambuły, patrz
 * {@link CAPO_MENTION_RE}. Zwraca `null`, gdy linia w ogóle nie wspomina kapodastra;
 * `{ fret: null }`, gdy wspomina, ale nie da się wyciągnąć numeru progu (autor nie
 * podał liczby - `compile.ts` NIE zgaduje, tylko ostrzega). */
function detectInformalCapo(line: string): { fret: number | null } | null {
  if (!CAPO_MENTION_RE.test(line)) return null;
  const digitMatch = line.match(CAPO_DIGIT_RE);
  if (digitMatch) return { fret: Number.parseInt(digitMatch[0], 10) };
  const romanMatch = line.match(CAPO_ROMAN_PROG_RE);
  if (romanMatch) {
    const fret = ROMAN_TO_ARABIC[romanMatch[1]!.toLowerCase()];
    if (fret !== undefined) return { fret };
  }
  return { fret: null };
}

// `E|---3---5---` i podobne: struna (litera A-G, opcjonalnie # albo b), potok, ciąg
// ładów/pauz/potoków, z opcjonalną notacją powtórzenia na końcu (` x4`). Bez flagi `i`
// biblioteka łapałaby to jako akord o rdzeniu z wielkiej litery struny (pułapka 3);
// my celowo dopasowujemy obie wielkości liter, żeby wyciąć linię całkowicie.
//
// KOREKTA 2026-08-27 (MH, na `wehikul-czasu.txt`): pierwotna klasa znaków `[0-9\-|]*`
// dopuszczała wyłącznie cyfry, myślniki i kreski, więc linie z oznaczeniami techniki
// gry (`h` hammer-on, `p` pull-off, `/` slide w górę, `\` slide w dół, `~` wibracja,
// `_` legato, `b`/`s` bend/slide, nawiasy przy akordach chwytanych częściowo) nie
// pasowały do wzorca i przechodziły dalej jako rzekome akordy. Klasa poszerzona,
// ale wciąż wymaga kreski pionowej zaraz po nazwie struny, żeby nie stać się workiem.
const TAB_LINE_RE = /^[A-Ga-g](#|b)?\|[-0-9|hpbsHPBS/\\~^*().<>_ ]*(?:\s+[xX]\d+)?\s*$/;

// KOREKTA 2026-08-27, druga runda (korpus angielski, shape-of-my-heart.txt):
// TAB_LINE_RE wymaga, żeby CAŁA linia była tabulaturą, ale autor dopisał do
// linii tabulatury wolny komentarz odsyłający do wcześniejszego wzoru, np.
// "B|---1---------|    AS ABOVE      AS ABOVE     AS ABOVE      AS ABOVE".
// Taka linia nie pasuje do TAB_LINE_RE (kończy się słowami, nie tabulaturą) ani
// do `looksLikeTabByDashDensity` (gęstość myślników spada poniżej progu przez
// doklejony tekst), więc przechodziła dalej i CHORD_LINE_REGEX biblioteki (bez
// flagi `i`) łapał "B" jako rdzeń akordu, a "AS"/"ABOVE" jako kolejne fałszywe
// akordy - dokładnie pułapka 3, złapana przez niezmiennik "żaden akord nie
// zawiera |" po rozszerzeniu korpusu o pliki angielskie. Prefiks "litera
// struny plus potok zaraz na początku linii" jest sam w sobie jednoznacznym
// sygnałem tabulatury: żadna prawdziwa linia akordów ani tekstu w formacie UG
// tak się nie zaczyna, więc cała linia z tym prefiksem jest tabulaturą
// NIEZALEŻNIE od tego, co następuje po pierwszym potoku.
const TAB_LINE_PREFIX_RE = /^[A-Ga-g](#|b)?\|/;

// Drugie, niezależne kryterium (ta sama korekta): wzorzec oparty o nazwę struny
// zawsze będzie za wąski wobec inwencji autorów tabów, np. linii bez nazwy struny na
// początku (kontynuacja poprzedniej, oznaczenia bez litery). Linia jest tabulaturą
// także wtedy, gdy ma co najmniej 8 znaków, zawiera ciąg 3+ myślników pod rząd, a
// myślniki stanowią ponad 30% jej długości. `\r` z zakończeń CRLF jest tu obcinany
// przed liczeniem, żeby nie zniekształcał proporcji o jeden znak.
function looksLikeTabByDashDensity(line: string): boolean {
  const clean = line.replace(/\r$/, '');
  if (clean.length < 8) return false;
  if (!clean.includes('---')) return false;
  const dashCount = (clean.match(/-/g) ?? []).length;
  return dashCount / clean.length > 0.3;
}

// Diagram chwytu: nazwa akordu, spacja, ciąg pozycji na progach rozdzielony
// myślnikami (litery strun E-A-D-G-B-e u góry, potem "3-x-0-0-3" itd. na dole),
// czasem z wariantem alternatywnym po "or" i objaśnieniem w nawiasie na końcu.
// Realny przypadek: perfect.txt ma tabelę chwytów wplecioną w preambułę, w tym
// FAŁSZYWY nagłówek "[Coda]" między liniami diagramu - bez wycięcia diagramu
// ta linia parsuje się jako czysty tekst (nie pasuje do CHORD_LINE_REGEX
// biblioteki), więc "sekcja" po [Coda] wyglądałaby na mającą treść, mimo że to
// tabela chwytów, nie muzyka. Wymaga co najmniej dwóch myślników (trzech
// pozycji), żeby nie złapać przypadkiem czegoś innego.
const CHORD_DIAGRAM_LINE_RE = /^\s*[A-G](#|b)?[^\s/|%()]*(\/[A-G](#|b)?[^\s/|%()]*)?\s+[\dxX]+(-[\dxX]+){2,}\b/u;

// "2x" → "x2": chordsheetjs@16.2.2 rozpoznaje wyłącznie notację z "x" na początku
// (zweryfikowane empirycznie: "Bm A D G A 2x" oddaje `chords: ""`, cała linia ląduje
// jako lyrics; "Bm A D G A x2" parsuje się poprawnie). Dopasowanie wymaga, żeby przed
// cyfrą nie stało nic "słownego" (unikamy np. wnętrza nazwy akordu) i żeby po "x" nie
// szła kolejna litera.
const REVERSED_REPEAT_RE = /(?<![\p{L}\d])(\d+)x(?![\p{L}])/gu;

// UWAGA: świadomie bez flagi `i`. Markup UG jest zawsze pisany małymi literami
// (`[tab]`, `[ch]`), a dopuszczenie wielkich liter kolidowałoby z legalnymi skrótami
// nagłówków w nawiasach, np. "[Ch]" (alias Chorus) zostałby błędnie rozpoznany jako
// znacznik akordu i wycięty do pustej linii.
/** Tab bez znaczników UG, do pokazania w edytorze. Kompilator zdejmuje je
 * jako pierwszy krok i nie korzysta z nich dalej, więc wynik kompilacji jest
 * ten sam (pilnuje tego test na korpusie w `compile.test.ts`). */
export function bezZnacznikowUg(text: string): string {
  return stripUgMarkup(text).text;
}

function stripUgMarkup(text: string): { text: string; chordTagCount: number } {
  const chordTagCount = (text.match(/\[ch\]/g) ?? []).length;
  const stripped = text.replace(/\[tab\]|\[\/tab\]|\[ch\]|\[\/ch\]/g, '');
  return { text: stripped, chordTagCount };
}

// Drugia grupa to opcjonalny dopisek opisowy w nawiasach OKRĄGŁYCH po nawiasie
// kwadratowym, np. "[Middle] (HARMONICA SOLO)" (shape-of-my-heart.txt). Celowo
// ograniczone do JEDNEJ grupy nawiasów okrągłych na końcu linii (nie do
// dowolnego tekstu), żeby nie zacząć łapać linii, które przypadkiem zaczynają
// się od nawiasu kwadratowego, a wcale nie są nagłówkiem z dopiskiem.
const BRACKET_LINE_RE = /^\s*\[\s*([^[\]]+?)\s*\]\s*(?:\(([^()]*)\))?\s*$/u;
const COLON_LINE_RE = /^\s*([\p{L}][\p{L} \-]*?)\s*:\s*$/u;

// Nagłówki porządkowe "1st:", "2nd:", "3rd:", "4th:" (znalezione w korpusie, nie
// wymyślone: linia "2nd:" w nie-placz-ewka.txt). Zaczynają się cyfrą, więc
// COLON_LINE_RE (wymaga litery na początku) ich nie łapie, stąd osobny wzorzec.
//
// Cała rodzina porządkowa jest rozpoznawana, WRAZ z "1st:". Wcześniejsza wersja
// zaczynała od n >= 2 i uzasadniała to tym, że w nie-placz-ewka.txt "1st:" opisuje
// rzekomo drugi raz tę samą zwrotkę co wcześniejszy "Verse:". To było nieprawdą i
// wzięło się z błędnego złotego wyniku, poprawionego 2026-08-27: "Verse:" w tym
// pliku to blok INSTRUKTAŻOWY (sama tabulatura arpeggio z akordami nad nią, zero
// tekstu), a "1st:" w linii 67 otwiera prawdziwą pierwszą zwrotkę ze słowami
// "Nie płacz Ewka bo tu miejsca brak". Blok instruktażowy zdejmuje teraz reguła
// w `segment.ts`, a nie wyłączenie jednej cyfry tutaj.
const ORDINAL_LINE_RE = /^\s*(\d+)(?:st|nd|rd|th)\s*:\s*$/iu;

// Powtórzenie zapisane wprost przy nagłówku, BEZ dwukropka i BEZ nawiasu, np.
// "Chorus x2" (linia 95 w nie-placz-ewka.txt, ostatnia linia pliku). Notacja "2x"
// jest już w tym miejscu zamieniona na "x2" przez REVERSED_REPEAT_RE wcześniej w
// potoku. Dopasowanie celowo wymaga "x<cyfry>" na końcu, żeby nie łapać zwykłych
// linii tekstu: bez tego warunku każde pojedyncze słowo byłoby podejrzane.
// Rzeczywiste linie akordów z notacją powtórzenia (np. "Bm A D G A x2") PRZECHODZĄ
// przez ten wzorzec składniowo, ale odpadają niżej w `matchHeaderLabel`, bo "bm a d
// g a" nie jest żadną znaną rodziną nagłówków - więc linia wraca nietknięta.
const BARE_REPEAT_LINE_RE = /^\s*([\p{L}][\p{L} \-]*?)\s+[xX](\d+)\s*$/u;

/** Zdejmuje z etykiety nagłówka ewentualne trafiające na koniec "xN", PRZED
 * dopasowaniem rodziny. Bez tego "Chorus x2" liczyłby "2" jako numer sekcji
 * (przez `numberMatch` w `matchHeaderLabel`), a nie jako powtórzenie. */
function extractTrailingRepeat(label: string): { core: string; times: number | null } {
  const match = label.match(/^(.*?)\s+[xX](\d+)\s*$/u);
  if (!match) return { core: label, times: null };
  return { core: match[1]!, times: Number.parseInt(match[2]!, 10) };
}

// Notacja taktowa: "|" oddziela takty, "/" jest uderzeniem w takcie (ten sam
// akord co poprzednie uderzenie, trwa dalej), "%" powtarza CAŁY poprzedni
// takt, a nawias okrągły wokół pojedynczego akordu oznacza akord domyślny
// albo wybrzmiewający (np. "(G)"). Realne przypadki z korpusu:
// - shape-of-my-heart.txt, "[Middle]": "A / / G / / Em7 / F#7 / ..." (same
//   ukośniki, bez kresek pionowych);
// - perfect.txt, "[Interlude]": "|(G) | G | Em | % |" (kreski i procent).
//
// Bez rozpoznania tych linii biblioteka nie widzi w nich ŻADNEGO akordu: ani
// "/" ani "|" ani "%" nie pasują do CHORD_LINE_REGEX, więc cała linia ląduje
// jako `lyrics`, i sekcja traci WSZYSTKIE akordy (zaobserwowane bezpośrednio:
// [Middle] i [Interlude] dawały `chords.length === 0`).
const CHORD_TOKEN_RE = /^[A-G](#|b)?[^\s/|%()]*(\/[A-G](#|b)?[^\s/|%()]*)?$/;

// Notacja "xN" bezpośrednio przy linii akordów, BEZ nagłówka: doklejona na końcu
// tej samej linii ("C G x4", zlecony przypadek dowodowy) albo stojąca samodzielnie
// w swojej linii, czasem w nawiasie okrągłym, tuż pod linią akordów (realny defekt
// wehikul-czasu.txt, sekcja outro: linia "A E D A", a pod nią osobna linia "(x2)").
// ChordSheetJS i tak wytnie taki ogon z linii akordów przy parsowaniu (nie trafia
// do `Progression.raw`), więc to NIE jest krok czyszczący tekst - tekst zostaje
// nietknięty, to tylko dodatkowy SYGNAŁ `times` sekcji, złapany PRZED oddaniem
// tekstu do parsera, zamiast dopiero z doklejonego ogona w `lyrics` (ten ogon i tak
// broni osobno `buildAnchor`/`stripGluedRepeat` w progression.ts).
const CHORD_LINE_TRAILING_REPEAT_RE = /^(.+?)\s+x(\d+)\s*$/iu;
const STANDALONE_REPEAT_LINE_RE = /^\(?x(\d+)\)?$/iu;

/** Rozpoznaje "xN" przy linii akordów (doklejone na końcu albo samodzielne),
 * patrz {@link CHORD_LINE_TRAILING_REPEAT_RE}. Zwraca `null`, gdy linia nie jest
 * żadnym z tych dwóch przypadków, albo gdy tokeny przed "xN" nie wyglądają na
 * akordy (żeby nie złapać przypadkiem zwykłego zdania kończącego się na "x2"). */
function detectChordLineRepeat(line: string): number | null {
  const trimmed = line.trim();
  if (trimmed === '') return null;
  const standalone = trimmed.match(STANDALONE_REPEAT_LINE_RE);
  if (standalone) return Number.parseInt(standalone[1]!, 10);
  const trailing = trimmed.match(CHORD_LINE_TRAILING_REPEAT_RE);
  if (!trailing) return null;
  const tokens = trailing[1]!.trim().split(/\s+/);
  if (tokens.length === 0 || !tokens.every((t) => CHORD_TOKEN_RE.test(t))) return null;
  return Number.parseInt(trailing[2]!, 10);
}

type MeasureToken =
  | { type: 'pipe' | 'slash' | 'percent' }
  | { type: 'chord'; value: string };

function classifyMeasureToken(token: string): MeasureToken | null {
  if (token === '|') return { type: 'pipe' };
  if (token === '/') return { type: 'slash' };
  if (token === '%') return { type: 'percent' };
  const parenMatch = token.match(/^\(([^()]+)\)$/);
  if (parenMatch) {
    return CHORD_TOKEN_RE.test(parenMatch[1]!) ? { type: 'chord', value: parenMatch[1]! } : null;
  }
  return CHORD_TOKEN_RE.test(token) ? { type: 'chord', value: token } : null;
}

/**
 * Próbuje przepisać linię w notacji taktowej na zwykły ciąg akordów
 * rozdzielony spacjami (to, co `CHORD_LINE_REGEX` biblioteki rozumie). Zdejmuje
 * `|` i `/` (bez zastępowania czymkolwiek - to nie są powtórzenia, tylko
 * granice taktu i wypełnienie rytmiczne), zdejmuje nawiasy z akordu
 * domyślnego, a `%` rozwija na powtórzenie OSTATNIEGO wypisanego akordu, bo to
 * jego znaczenie ("powtórz poprzedni takt"). Rytm harmoniczny (bary grane
 * kilka razy z rzędu) zostaje więc widoczny w wyniku, zgodnie z zasadą, że
 * `Progression.raw` nie dedupeuje sąsiadów.
 *
 * Zwraca `null`, gdy linia NIE jest notacją taktową, żeby nie tknąć zwykłej,
 * wyrównanej kolumnowo linii akordów nad tekstem (taka linia nie zawiera ani
 * `|`, ani samodzielnego `/`, ani `%`, więc warunek niżej ją bezpiecznie
 * odrzuca) - inaczej ten mechanizm zniszczyłby wyrównanie akordów nad wersem,
 * potrzebne bibliotece do sparowania `ChordLyricsPair`.
 */
function tryRewriteMeasureLine(line: string): string | null {
  if (!/[|/%]/.test(line)) return null;

  const spaced = line.replace(/\|/g, ' | ');
  const rawTokens = spaced.trim().split(/\s+/).filter((t) => t !== '');
  if (rawTokens.length === 0) return null;

  const classified: MeasureToken[] = [];
  for (const raw of rawTokens) {
    const token = classifyMeasureToken(raw);
    if (token === null) return null; // choć jedno słowo nie pasuje - to nie jest notacja taktowa
    classified.push(token);
  }

  const chordCount = classified.filter((t) => t.type === 'chord').length;
  if (chordCount < 2) return null;
  const hasStructuralToken = classified.some((t) => t.type === 'pipe' || t.type === 'slash' || t.type === 'percent');
  if (!hasStructuralToken) return null;

  const out: string[] = [];
  let last: string | null = null;
  for (const token of classified) {
    if (token.type === 'chord') {
      out.push(token.value);
      last = token.value;
    } else if (token.type === 'percent' && last !== null) {
      out.push(last);
    }
    // pipe/slash: pomijane, bez zastępnika.
  }
  if (out.length === 0) return null;
  return out.join(' ');
}

export function preNormalize(rawTab: string): PreNormalizeResult {
  const { text: disarmed, chordTagCount } = stripUgMarkup(rawTab);
  const withFixedRepeats = disarmed.replace(REVERSED_REPEAT_RE, 'x$1');

  const inputLines = withFixedRepeats.split('\n');
  const outputLines: string[] = [];
  const headers: HeaderHit[] = [];
  let strumming: string | null = null;
  let tabLinesRemoved = 0;
  let chordDiagramLinesRemoved = 0;
  // Indeks bieżącego nagłówka w `headers` (-1 = jeszcze preambuła, przed pierwszym
  // rozpoznanym nagłówkiem). Używany do zbierania SYGNAŁU "xN" z linii akordów
  // (patrz `chordLineTimesByHeader` niżej): sekcja jest wtedy jednoznacznie ta,
  // którą właśnie otworzył ostatni napotkany nagłówek.
  let currentHeaderIndex = -1;
  let informalCapo: { fret: number | null } | null = null;
  // "xN" wyciągnięte z linii akordów (nie z nagłówka), per indeks nagłówka. Zawsze
  // NADPISYWANE przy kolejnym trafieniu w tej samej sekcji (ostatnie wygrywa), a
  // stosowane dopiero PO całej pętli i TYLKO gdy nagłówek sam nie miał własnego "xN"
  // (nagłówek ma zawsze pierwszeństwo nad linią akordów).
  const chordLineTimesByHeader = new Map<number, number>();

  for (const line of inputLines) {
    if (currentHeaderIndex === -1 && informalCapo === null) {
      const capoHit = detectInformalCapo(line);
      if (capoHit) informalCapo = capoHit;
    }

    const strummingMatch = line.match(STRUMMING_LINE_RE);
    if (strummingMatch) {
      if (strumming === null) strumming = strummingMatch[2]!.trim();
      continue;
    }

    if (TAB_LINE_RE.test(line) || looksLikeTabByDashDensity(line) || TAB_LINE_PREFIX_RE.test(line)) {
      tabLinesRemoved += 1;
      if (currentHeaderIndex >= 0) headers[currentHeaderIndex]!.hadTabLinesRemoved = true;
      continue;
    }

    if (CHORD_DIAGRAM_LINE_RE.test(line)) {
      chordDiagramLinesRemoved += 1;
      continue;
    }

    const bracketMatch = line.match(BRACKET_LINE_RE);
    if (bracketMatch) {
      const { core, times } = extractTrailingRepeat(bracketMatch[1]!);
      const match = matchHeaderLabel(core, true);
      if (match) {
        const description = bracketMatch[2] !== undefined && bracketMatch[2].trim() !== ''
          ? bracketMatch[2].trim()
          : null;
        headers.push({
          lineIndex: outputLines.length,
          original: bracketMatch[1]!,
          kind: match.kind,
          number: match.number,
          label: DISPLAY_LABEL[match.kind](match.number),
          isTypo: match.isTypo,
          times,
          description,
          hadTabLinesRemoved: false,
        });
        currentHeaderIndex = headers.length - 1;
        outputLines.push(canonicalHeaderText(match.kind, match.kind === 'verse' ? match.number : null));
        continue;
      }
      outputLines.push(line);
      continue;
    }

    const ordinalMatch = line.match(ORDINAL_LINE_RE);
    if (ordinalMatch) {
      const number = Number.parseInt(ordinalMatch[1]!, 10);
      if (number >= 1) {
        headers.push({
          lineIndex: outputLines.length,
          original: line.trim().replace(/:$/, ''),
          kind: 'verse',
          number,
          label: DISPLAY_LABEL.verse(number),
          isTypo: false,
          times: null,
          description: null,
          hadTabLinesRemoved: false,
        });
        currentHeaderIndex = headers.length - 1;
        outputLines.push(canonicalHeaderText('verse', number));
        continue;
      }
    }

    const colonMatch = line.match(COLON_LINE_RE);
    if (colonMatch) {
      const original = colonMatch[1]!.trim();
      const { core, times } = extractTrailingRepeat(original);
      const match = matchHeaderLabel(core, false);
      if (match) {
        headers.push({
          lineIndex: outputLines.length,
          original,
          kind: match.kind,
          number: match.number,
          label: DISPLAY_LABEL[match.kind](match.number),
          isTypo: match.isTypo,
          times,
          description: null,
          hadTabLinesRemoved: false,
        });
        currentHeaderIndex = headers.length - 1;
        outputLines.push(canonicalHeaderText(match.kind, match.kind === 'verse' ? match.number : null));
        continue;
      }
      // Nie pasuje do żadnej znanej rodziny (np. "1st:", "Tab by:") - to
      // metadana albo zwykły tekst, obsłużone niżej albo zostawione bez zmian.
    }

    const bareRepeatMatch = line.match(BARE_REPEAT_LINE_RE);
    if (bareRepeatMatch) {
      const original = bareRepeatMatch[1]!.trim();
      const times = Number.parseInt(bareRepeatMatch[2]!, 10);
      const match = matchHeaderLabel(original, false);
      if (match) {
        headers.push({
          lineIndex: outputLines.length,
          original,
          kind: match.kind,
          number: match.number,
          label: DISPLAY_LABEL[match.kind](match.number),
          isTypo: match.isTypo,
          times,
          description: null,
          hadTabLinesRemoved: false,
        });
        currentHeaderIndex = headers.length - 1;
        outputLines.push(canonicalHeaderText(match.kind, match.kind === 'verse' ? match.number : null));
        continue;
      }
      // Składniowo pasuje (np. "Bm A D G A x2"), ale "bm a d g a" nie jest żadną
      // znaną rodziną nagłówków - to linia akordów z notacją powtórzenia, nie
      // nagłówek. Zostaje nietknięta, obsłuży ją dalej preNormalize/parser.
    }

    const metaMatch = line.match(METADATA_LINE_RE);
    if (metaMatch) {
      const canonicalLabel = METADATA_MAP[metaMatch[1]!.toLowerCase()]!;
      outputLines.push(`${canonicalLabel}: ${metaMatch[2]}`);
      continue;
    }

    const measureRewrite = tryRewriteMeasureLine(line);
    if (measureRewrite !== null) {
      outputLines.push(measureRewrite);
      continue;
    }

    // Sygnał "xN" z samej linii akordów (bez zmiany tekstu), patrz
    // {@link detectChordLineRepeat}. Zbierany tylko wewnątrz sekcji (nie w
    // preambule), zastosowany dopiero po pętli, żeby nagłówek zawsze mógł
    // wygrać, niezależnie od kolejności.
    if (currentHeaderIndex >= 0) {
      const chordLineTimes = detectChordLineRepeat(line);
      if (chordLineTimes !== null) chordLineTimesByHeader.set(currentHeaderIndex, chordLineTimes);
    }

    outputLines.push(line);
  }

  for (let i = 0; i < headers.length; i += 1) {
    const header = headers[i]!;
    if (header.times === null) {
      const inferred = chordLineTimesByHeader.get(i);
      if (inferred !== undefined) header.times = inferred;
    }
  }

  return {
    text: outputLines.join('\n'),
    headers,
    strumming,
    informalCapo,
    tabLinesRemoved,
    chordDiagramLinesRemoved,
    chordTagCount,
  };
}
